import { useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  BulkAssignEmployeesToLocationDocument,
  GetLocationsDocument,
  LocationWithEmployeesDocument,
  LocationsEmployeesCollectionListDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

interface Props {
  locationId: string
  onClose: () => void
}

const AddEmployee = ({ onClose, locationId }: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()
  const apolloClient = useApolloClient()
  const { loading, data } = useQuery(LocationWithEmployeesDocument, {
    variables: { ids: [locationId] },
    fetchPolicy: 'network-only',
  })
  const [employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })

  const location = data?.locations?.locationsConnection?.nodes?.find(
    (loc: { id: string }) => loc.id === locationId
  )

  const currentEmployeesIds: string[] =
    location?.employees?.map((employee: { id: string }) => employee.id) || []

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!location) throw new Error('Unknown location')

    const { success } = await asyncAction(() =>
      apolloClient.mutate({
        mutation: BulkAssignEmployeesToLocationDocument,
        variables: {
          locationId: location.id,
          employeeSelection: employeeSelection.type !== 'manual' ? employeeSelection : undefined,
          employeeIds: employeeSelection.type === 'manual' ? employeeSelection.ids : [],
        },
      })
    )

    if (success) {
      apolloClient.refetchQueries({
        include: [
          LocationWithEmployeesDocument,
          GetLocationsDocument,
          LocationsEmployeesCollectionListDocument,
        ],
      })

      closePortal()
    } else {
      // Workaround for a more descriptive error until product handles the issue with the manager permissions
      app.setFlash({
        type: 'wrong',
        message: i18n.t('settings.locations.location.employees.add.error_permissions'),
        time: 3000,
      })
    }
  }

  if (loading || !location) return null

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <Modal
        illustration="user-male-female"
        title={i18n.t('settings.locations.location.employees.add.title', {
          name: location.name,
        })}
        description={i18n.t('settings.locations.location.employees.add.description')}
        onClose={onClose}
        onSubmit={onSubmit}
        disableFocusTrap
        footer={
          <Button
            disabled={employeeSelection.type === 'manual' && employeeSelection.ids.length === 0}
            submit
          >
            {i18n.t('settings.locations.location.employees.add.submit')}
          </Button>
        }
      >
        <Box maxWidth="s400">
          <EmployeeSelectorV2
            multiple
            onChange={setEmployeeSelection}
            forcedFilters={{
              excludeIds: currentEmployeesIds,
              excludeCurrentEmployee: true,
            }}
          />
        </Box>
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default observer(AddEmployee)
